<template>
  <div>
    <vxe-grid
      ref="skuTable"
      auto-resize
      highlight-hover-row
      resizable
      size="mini"
      cell-class-name="align-top"
      row-id="amzReplenishmentDetailCode"
      :toolbar-config="tableToolbar"
      :data="tableData"
      :columns="tableColumns"
      :loading="loading"
      :height="tableHeight - 23"
      :seq-config="{ startIndex: (tablePage.curPage - 1) * tablePage.pageSize }"
      :checkbox-config="{
        highlight: true,
        reserve: true,
        checkMethod: ({ row }) => row.approvalStatus === 'S' && !disabled,
      }"
      @checkbox-change="checkboxChange"
      @checkbox-all="checkboxChange"
      @cell-click="cellClick"
    >
      <template v-slot:toolbar_form>
        <slot name="header"></slot>
      </template>
      <template v-slot:pager>
        <slot name="pager"></slot>
      </template>
      <template v-slot:opt="{ row }">
        <div
          style="text-align: center"
          class="mt-2"
          v-if="row.approvalStatus === 'S'"
        >
          <el-dropdown trigger="click" @command="optCommand">
            <span class="el-dropdown-link">
              {{ $l('INDEX.choose_fabric_function') || '功能' }}
              <i class="el-icon-arrow-down el-icon--right"></i>
            </span>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item
                v-for="(opt, index) in tableOpts"
                :key="index"
                :icon="opt.icon"
                :command="{ opt, data: row }"
                :divided="opt.divided"
              >
                {{ opt.text }}
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </template>
      <template v-slot:defaultImage="{ row: { defaultImageUrl } }">
        <el-image
          :src="defaultImageUrl || '/image/static/img/nopic.jpg'"
          :preview-src-list="[defaultImageUrl || '/image/static/img/nopic.jpg']"
          style="width:60px; height:60px"
        >
          <div slot="error" class="image-slot">
            <img
              src="/image/static/img/nopic.jpg"
              style="width: 60px; height: 60px"
            />
          </div>
        </el-image>
      </template>

      <template v-slot:displaySku="{ row }">
        <TableLabel :value="row.goodsName" />
        <TableLabel :value="row.goodsNameEn" />
        <TableLabel
          label="SIZE"
          labelKey="table_label_goodsSize"
          :value="row.goodsSize"
        />
        <TableLabel
          label="分类"
          labelKey="table_label_masterCategoryName"
          :value="row.masterCategoryName"
        />
        <TableLabel
          label="SKU"
          labelKey="table_label_amzSkuNo"
          :value="row.amzSkuNo"
          primary
        />
        <div class="d-flex justify-content-between align-items-center">
          <div class="flex-1">
            <TableLabel
              v-if="isVc === 'Y'"
              label="VC PO"
              labelKey="table_label_vcPo"
              :value="row.vcPo"
              primary
            />
          </div>
          <div>
            <el-tag
              v-if="row.skuSalesChannel === 'V'"
              size="mini"
              type="warning"
            >
              {{ row.v_skuSalesChannel }}
            </el-tag>
          </div>
        </div>
        <div class="d-flex align-items-center" v-if="isVc !== 'Y'">
          <el-tag
            size="mini"
            :type="
              row.approvalStatus === 'N' || row.approvalStatus === 'C'
                ? 'primary'
                : row.approvalStatus === 'S' || row.approvalStatus === 'R'
                ? 'warning'
                : row.approvalStatus === 'Y' || row.approvalStatus === 'Q'
                ? 'success'
                : row.approvalStatus === 'D' || row.approvalStatus === 'B'
                ? 'danger'
                : 'primary'
            "
          >
            {{ row.v_approvalStatus }}
          </el-tag>
          <el-link
            v-if="row.approvalComments"
            type="danger"
            :underline="false"
            style="font-size: 13px;"
            class="ml-2"
            @click="showApprovalComments(row)"
          >
            {{ $l('btn_showApprovalComments') || '查看驳回原因' }}
          </el-link>
        </div>
      </template>

      <template v-slot:displayStore="{ row }">
        <TableLabel
          label="店铺"
          labelKey="table_label_invStoreName"
          :value="row.invStoreName"
        />
        <TableLabel
          label="Store SKU"
          labelKey="table_label_amzSkuStoreNo"
          :value="row.amzSkuStoreNo"
          primary
        />
        <TableLabel
          label="ASIN"
          labelKey="table_label_asinCode"
          :value="row.asinCode"
          primary
        />
        <TableLabel
          label="FNSKU"
          labelKey="table_label_fnskuCode"
          :value="row.fnskuCode"
          primary
        />
        <TableLabel
          label="UPC"
          labelKey="table_label_upc"
          :value="row.upc"
          primary
        />
        <TableLabel
          label="运营"
          labelKey="table_label_operateName"
          :value="row.operateName"
        />
        <el-tag
          v-if="row.v_storeSkuStatus"
          size="mini"
          :type="
            row.storeSkuStatus === 'Y'
              ? 'success'
              : row.storeSkuStatus === 'S' ||
                row.storeSkuStatus === 'B' ||
                row.storeSkuStatus === 'W'
              ? 'danger'
              : row.storeSkuStatus === 'X'
              ? 'warning'
              : ''
          "
        >
          {{ row.v_storeSkuStatus }}
        </el-tag>
      </template>

      <template v-slot:displaySubmitQty="{ row }">
        <TableLabel
          label="补货数量"
          labelKey="table_label_submitQty"
          :value="row.submitQty || '0'"
          primary
        />
        <TableLabel
          label="装箱入数"
          labelKey="table_label_unitInBox"
          :value="row.unitInBox"
        />
        <TableLabel
          label="补货箱数"
          labelKey="table_label_ctnQty"
          :value="row.submitQty / row.unitInBox"
          primary
        />
      </template>

      <template v-slot:displayInv="{ row }">
        <div class="d-flex">
          <div style="flex:1">
            <TableLabel
              label="FBA库存"
              labelKey="table_label_fbaQty"
              :value="row.fbaQty || '0'"
            />
            <TableLabel
              label="国内库存"
              labelKey="table_label_totalInvQty"
              :value="row.totalInvQty || '0'"
            />
            <TableLabel
              label="预计可售天数"
              labelKey="table_label_estmatDaysOfSupply"
              :value="row.estmatDaysOfSupply"
            />
            <TableLabel
              label="预计断货日期"
              labelKey="table_label_estmatSoldoutDate"
              :value="row.estmatSoldoutDate"
            />
          </div>
          <div>
            <div class="alert-level-tag">
              <span :style="{ backgroundColor: row.alertLevelColor }" />
              <span :style="{ backgroundColor: row.alertLevelColor }" />
            </div>
          </div>
        </div>
      </template>

      <template v-slot:displayOperate="{ row }">
        <TableLabel
          label="预计上架日期"
          labelKey="table_label_etaDate"
          :value="row.etaDate"
        />
        <TableLabel
          label="中途断货天数"
          labelKey="table_label_halfwayOutageDays"
          :value="row.halfwayOutageDays || '0'"
        />
        <TableLabel
          label="实际日销售量"
          labelKey="table_label_advQtyBeforeRepPerDay"
          :value="row.advQtyBeforeRepPerDay || '0'"
        />
        <TableLabel
          label="到货日预估日销"
          labelKey="table_label_soldQtyAfterRepPerDay"
          :value="row.soldQtyAfterRepPerDay || '0'"
        />
        <TableLabel
          label="到货日活动销量"
          labelKey="table_label_advQtyAfterRepPerDay"
          :value="row.advQtyAfterRepPerDay || '0'"
        />
      </template>

      <template v-slot:displayDaySalesVolume="{ row }">
        <TableLabel
          label="前30天日销量"
          labelKey="table_label_soldQtyLast4WkPerDay"
          :value="row.soldQtyLast4WkPerDay || '0'"
        />
        <TableLabel
          label="前15天日销量"
          labelKey="table_label_soldQtyLast15Day"
          :value="row.soldQtyLast15Day || '0'"
        />
        <TableLabel
          label="前7天日销量"
          labelKey="table_label_soldQtyLast7Day"
          :value="row.soldQtyLast7Day || '0'"
        />
        <TableLabel
          label="前3天日销量"
          labelKey="table_label_soldQtyLast3Day"
          :value="row.soldQtyLast3Day || '0'"
        />
      </template>

      <template v-slot:status="{ row }">
        <TableLabel
          label="审批人"
          labelKey="table_label_approvalByName"
          :value="row.approvalByName"
        />
        <TableLabel
          label="审批时间"
          labelKey="table_label_approvedTime"
          :value="row.approvedTime"
        />
        <div class="d-flex align-items-center">
          <el-tag
            size="mini"
            :type="
              row.approvalStatus === 'N' || row.approvalStatus === 'C'
                ? 'primary'
                : row.approvalStatus === 'S' || row.approvalStatus === 'R'
                ? 'warning'
                : row.approvalStatus === 'Y' || row.approvalStatus === 'Q'
                ? 'success'
                : row.approvalStatus === 'D' || row.approvalStatus === 'B'
                ? 'danger'
                : 'primary'
            "
          >
            {{ row.v_approvalStatus }}
          </el-tag>
          <el-link
            v-if="row.approvalComments"
            type="danger"
            :underline="false"
            style="font-size: 13px;"
            class="ml-2"
            @click="showApprovalComments(row)"
          >
            {{ $l('btn_showApprovalComments') || '查看驳回原因' }}
          </el-link>
        </div>
      </template>

      <template v-slot:modifyInfo="{ row }">
        <TableLabel :value="row.modifyByName" />
        <TableLabel :value="row.modifyDate" />
      </template>
      <template v-slot:empty="{}">
        <div style="text-align: center">
          <img src="/image/static/img/nodata.png" />
          <p class="no-data">
            {{ $l('INDEX.tips_no_data') || '暂无数据！' }}
          </p>
        </div>
      </template>
    </vxe-grid>
  </div>
</template>
<script>
import TableAutoHeight from '@/mixins/table-auto-height'
import EditLoading from '@/mixins/edit-loading'
import tableColumns from './table-columns'
import tableOpts from './table-opts'
import TableLabel from './label.vue'
export default {
  mixins: [TableAutoHeight, EditLoading],
  components: { TableLabel },
  props: {
    tableData: { type: Array, default: () => [] },
    tablePage: { type: Object, default: () => {} },
    loading: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    isVc: { type: String, default: '' },
  },
  data() {
    return {
      tableToolbar: {
        slots: { buttons: 'toolbar_form' },
        refresh: { query: () => this.$emit('reload') },
        zoom: true,
        custom: true,
      },
      tableOpts: tableOpts(this),
    }
  },
  computed: {
    tableColumns() {
      return tableColumns(this)
    },
  },
  watch: {
    isVc() {
      this.$refs.skuTable.refreshColumn()
    },
  },
  methods: {
    checkboxChange({ records, reserves }) {
      this.$emit('checkbox-change', records.concat(reserves))
    },
    clearCheckboxRows() {
      this.$refs.skuTable.clearCheckboxRow()
      this.$refs.skuTable.clearCheckboxReserve()
      this.$emit('checkbox-change', [])
    },
    cellClick({ row, column }) {
      if (this.disabled) return
      const { property } = column
      const filterColumns = ['checkbox', 'col-opt', 'defaultImageUrl']
      if (filterColumns.includes(property)) return

      const $table = this.$refs.skuTable
      $table.toggleCheckboxRow(row)

      const data = $table
        .getCheckboxRecords(true)
        .concat($table.getCheckboxReserveRecords(true))
      this.$emit('checkbox-change', data)
    },
    optCommand({ opt, data }) {
      const { menuType } = opt
      if (!menuType) return
      switch (menuType) {
        case 'approved':
        case 'rejected':
          this.$emit(menuType, data)
          break
        default:
          console.error('menuType configuration error: ', menuType)
      }
    },
    showApprovalComments({ approvalComments }) {
      this.$alert(
        approvalComments,
        this.$l('alert_title_approvalComments') || '驳回原因',
        {
          showCancelButton: true,
          showConfirmButton: false,
          cancelButtonText: this.$l('INDEX.btn_cancel') || '关闭',
        }
      )
    },
  },
}
</script>
